// ---------------------------------------------------------------------------
// RH · Recrutamento & Seleção — formatação/conversão de exibição.
//
// `scheduled_at` trafega em ISO-8601 COM offset (-03:00) para o backend; a
// resposta volta em ISO/UTC. O fuso do negócio é fixo America/Sao_Paulo (BRT,
// -03:00). Estas helpers centralizam a conversão ida/volta e as máscaras de
// exibição (CPF, datas, iniciais).
// ---------------------------------------------------------------------------

const BUSINESS_TIMEZONE = 'America/Sao_Paulo';
const OLD_REGISTRATION_DAYS = 90;

// datetime-local ("YYYY-MM-DDTHH:mm") → ISO com offset fixo BRT (-03:00).
export function toIsoWithOffset(localValue) {
   if (!localValue) return null;
   const withSeconds = /T\d{2}:\d{2}:\d{2}/.test(localValue)
      ? localValue
      : `${localValue}:00`;
   return `${withSeconds}-03:00`;
}

// ISO/UTC do backend → valor de <input type="datetime-local"> ("YYYY-MM-DDTHH:mm")
// no fuso do negócio, para editar/remarcar sem shift.
export function toDateTimeLocalValue(iso) {
   if (!iso) return '';
   const date = new Date(iso);
   if (Number.isNaN(date.getTime())) return '';
   const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: BUSINESS_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
   }).formatToParts(date);
   const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
   return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

// ISO → "dd/mm/aaaa hh:mm" (BRT).
export function formatDateTimeBr(iso) {
   if (!iso) return '—';
   const date = new Date(iso);
   if (Number.isNaN(date.getTime())) return '—';
   return date.toLocaleString('pt-BR', {
      timeZone: BUSINESS_TIMEZONE,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
   });
}

// ISO ou date-only → "dd/mm/aaaa". Trata "YYYY-MM-DD" sem shift de fuso.
export function formatDateBr(value) {
   if (!value) return '—';
   const raw = String(value);
   if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [year, month, day] = raw.split('-');
      return `${day}/${month}/${year}`;
   }
   const date = new Date(raw);
   if (Number.isNaN(date.getTime())) return '—';
   return date.toLocaleDateString('pt-BR', { timeZone: BUSINESS_TIMEZONE });
}

// Badge "cadastro antigo": candidato no banco há mais de 90 dias.
export function isOldRegistration(createdAt) {
   if (!createdAt) return false;
   const date = new Date(createdAt);
   if (Number.isNaN(date.getTime())) return false;
   const ageMs = Date.now() - date.getTime();
   return ageMs > OLD_REGISTRATION_DAYS * 24 * 60 * 60 * 1000;
}

// CPF é armazenado só com dígitos; formata na exibição (xxx.xxx.xxx-xx).
export function formatCpf(value) {
   const digits = String(value ?? '').replace(/\D/g, '').slice(0, 11);
   if (!digits) return '—';
   if (digits.length !== 11) return digits;
   return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

// Telefone BR (10/11 dígitos) para exibição; fallback pro valor cru.
export function formatPhoneBr(value) {
   const digits = String(value ?? '').replace(/\D/g, '');
   if (!digits) return '—';
   if (digits.length === 11) {
      return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
   }
   if (digits.length === 10) {
      return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
   }
   return value;
}

// Iniciais para avatar (primeiro + último nome).
export function initialsOf(name) {
   const parts = String(name ?? '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
   if (!parts.length) return '?';
   if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
   return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
